import React from "react";
import { Checkbox, InputNumber, Spin, message } from "antd";
import { FiSave, FiShoppingCart } from "react-icons/fi";
import { MdOutlineMedicalServices } from "react-icons/md";
import { getImg } from "../../../../Api/getImg";
import { formatDateTime } from "../../../../utils/helpers";
import drEasyApi from "../../../../Api/Customapi";

export default function FinancialNotesTab({
  patientData,
  orderNotes,
  setOrderNotes,
  doctorServices,
  selectedServices,
  setSelectedServices,
  servicesLoading,
  submittingOrder,
  setSubmittingOrder,
  existingOrderReport,
  loadingOrderReport,
  fetchOrderReport,
}) {
  const handleServiceToggle = (serviceId, service) => {
    setSelectedServices((prev) => {
      const next = { ...prev };
      if (next[serviceId]) {
        delete next[serviceId];
      } else {
        next[serviceId] = {
          serviceId,
          quantity: 1,
          note: "",
          unitPrice: service.priceAfterDiscount ?? service.price ?? 0,
          title: service.title,
        };
      }
      return next;
    });
  };

  const updateServiceQuantity = (serviceId, quantity) =>
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], quantity: quantity || 1 },
    }));

  const updateServiceNote = (serviceId, note) =>
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], note },
    }));

  const handleAddOrderReport = async () => {
    if (!patientData?.bookingId) {
      message.error("No booking ID found");
      return;
    }
    const servicesArray = Object.values(selectedServices).map((s) => ({
      serviceId: s.serviceId,
      quantity: s.quantity,
      note: s.note || "",
    }));
    setSubmittingOrder(true);
    try {
      await drEasyApi.post("api/Reports/OrderReport", {
        data: { bookingId: patientData.bookingId, notes: orderNotes, services: servicesArray },
      });
      message.success("Order report submitted successfully");
      setSelectedServices({});
      await fetchOrderReport();
    } catch {
      message.error("Failed to submit order report");
    } finally {
      setSubmittingOrder(false);
    }
  };

  if (loadingOrderReport) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  if (existingOrderReport) {
    return (
      <div className="bg-primary/10 rounded-xl p-6 border border-primary-300 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">Existing Order Report</h3>
          <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
            Submitted
          </span>
        </div>
        <div className="bg-white rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Created At</p>
              <p className="font-semibold text-gray-900">
                {formatDateTime(`${existingOrderReport.createdAt}Z`)}
              </p>
            </div>
          </div>
          {existingOrderReport.notes && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Order Notes</p>
              <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                {existingOrderReport.notes}
              </p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-600 mb-3 font-semibold">Services Ordered</p>
            <div className="space-y-2">
              {existingOrderReport.services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  <h4 className="font-semibold text-gray-900">{service.serviceTitle}</h4>
                  <p className="text-sm text-gray-600">Quantity: {service.quantity}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Order notes */}
      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 py-2 border-2 border-primary/20">
        <label className="block text-lg font-semibold text-gray-800 mb-3">
          Order Notes
        </label>
        <textarea
          className="w-full min-h-[120px] p-4 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 focus:ring-2 focus:ring-primary transition-all resize-y bg-white"
          onChange={(e) => setOrderNotes(e.target.value)}
          value={orderNotes}
          placeholder="Add any special instructions or notes for this order..."
        />
      </div>

      {/* Services */}
      <div>
        <div className="flex items-center justify-between my-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FiShoppingCart className="text-primary" size={24} />
            Select Services
          </h2>
          {Object.keys(selectedServices).length > 0 && (
            <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
              {Object.keys(selectedServices).length} Selected
            </span>
          )}
        </div>

        {servicesLoading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
            {doctorServices?.length > 0 ? (
              <div className="space-y-6">
                {doctorServices.map((clinic) => (
                  <div key={clinic.clinicId ?? clinic.clinicName}>
                    <h3 className="text-sm font-bold text-primary uppercase tracking-wider mb-2 px-1">
                      {clinic.clinicName}
                    </h3>
                    <div className="space-y-3">
                      {clinic.services.map((service) => (
                        <div
                          key={service.id}
                          className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-lg transition-all duration-300 border-2 ${selectedServices[service.id] ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary/50"}`}
                        >
                          <div className="flex items-start gap-4">
                            <Checkbox
                              checked={!!selectedServices[service.id]}
                              onChange={() => handleServiceToggle(service.id, service)}
                              className="mt-1"
                            />
                            {service.photoID ? (
                              <img
                                src={getImg(service.photoID)}
                                className="w-16 h-16 rounded-lg object-cover shadow-md flex-shrink-0"
                                alt={service.title}
                              />
                            ) : (
                              <MdOutlineMedicalServices className="text-primary w-10 h-10 md:w-14 md:h-14" />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-lg">
                                {service.title}
                              </h3>
                              {service.offerPercentage ? (
                                <div className="flex items-center gap-2">
                                  <p className="text-gray-400 line-through text-sm">
                                    EGP {service.price?.toFixed(2)}
                                  </p>
                                  <p className="text-primary font-bold text-lg">
                                    EGP {service.priceAfterDiscount?.toFixed(2)}
                                  </p>
                                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                                    -{service.offerPercentage}%
                                  </span>
                                </div>
                              ) : (
                                <p className="text-primary font-bold text-lg">
                                  {service.price != null
                                    ? `EGP ${service.price.toFixed(2)}`
                                    : "Price on request"}
                                </p>
                              )}
                              {selectedServices[service.id] && (
                                <div className="mt-4 space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Quantity
                                      </label>
                                      <InputNumber
                                        min={1}
                                        max={100}
                                        value={selectedServices[service.id].quantity}
                                        onChange={(value) => updateServiceQuantity(service.id, value)}
                                        className="w-full"
                                        size="large"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Service Note (Optional)
                                      </label>
                                      <textarea
                                        value={selectedServices[service.id].note || ""}
                                        onChange={(e) => updateServiceNote(service.id, e.target.value)}
                                        placeholder="Add specific notes for this service..."
                                        className="w-full px-3 py-1.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/50 transition-all resize-none"
                                        rows={1}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-2">
                <p className="md:text-lg font-semibold">No services available</p>
                <p className="text-sm text-gray-400 mt-2">Please check back later</p>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={handleAddOrderReport}
        disabled={submittingOrder}
        className="w-90 mt-3 self-end flex items-center justify-center gap-2 bg-primary text-white px-3 py-2 rounded-lg hover:bg-primary/90 transition-all font-semibold text-lg shadow hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FiSave size={16} />
        {submittingOrder ? "Submitting ..." : "Submit"}
      </button>
    </div>
  );
}